import { useParams } from 'react-router-dom'

import {
  UserRole,
  ProjectRole,
  SubscriptionStatus,
  FormStatus,
  Form,
  FormModelValidationMode,
  ProjectUserRbac,
} from '@types'

import { useMe } from '../user/me.hook'
import { useHasRoles } from '../user/has-roles.hook'
import { useProjectWithSubscription } from './project-with-subscription.hook'

export const useProjectGuards = (params?: {
  projectId?: string
}): {
  getProjectGuards: () => {
    isProjectSubscriptionActive: boolean
    canEditProject: boolean
    canManageFolderAccess: boolean
    canViewPlanning: boolean
    canViewObservations: boolean
    canViewForms: boolean
    canCommentTask: boolean
    canCommentForm: boolean
    canCommentObservation: boolean
    canEditTaskStatus: boolean
    canEditTask: boolean
    canEditForm: boolean
    canEditFormValues: (form: Form) => boolean
    canEditFormStatus: (form: Form) => boolean
    canEditObservation: boolean
    canEditObservationStatus: boolean
    canEditUserRbac: (userRbacTarget: ProjectUserRbac) => boolean
    isUserHasQrCodeRole: boolean
  }
  loading: boolean
} => {
  const { projectId: projectIdParam } = useParams()
  const { projectId = projectIdParam } = params || {}

  if (!projectId) {
    throw new Error('"projectId" must be provided to use "useGuardProject"')
  }

  const { data, loading: projectLoading } = useProjectWithSubscription(projectId)
  const project = data?.project

  const { getMe, loading: meLoading } = useMe()
  const me = getMe()

  const { hasRoles } = useHasRoles()

  const getProjectGuards = (): {
    isProjectSubscriptionActive: boolean
    canEditProject: boolean
    canManageFolderAccess: boolean
    canViewPlanning: boolean
    canViewObservations: boolean
    canViewForms: boolean
    canCommentTask: boolean
    canCommentForm: boolean
    canCommentObservation: boolean
    canEditTaskStatus: boolean
    canEditTask: boolean
    canEditForm: boolean
    canEditFormValues: (form: Form) => boolean
    canEditFormStatus: (form: Form) => boolean
    canEditObservation: boolean
    canEditObservationStatus: boolean
    canEditUserRbac: (userRbacTarget: ProjectUserRbac) => boolean
    isUserHasQrCodeRole: boolean
  } => {
    const isUserHasUserRoleAdmin =
      project?.authorId == me.id &&
      hasRoles({
        userRoles: [UserRole.RoleAdmin],
      })

    const isUserHasQrCodeRole = hasRoles({
      userRoles: [UserRole.RolePublic],
      projectRoles: [ProjectRole.RoleProjectQrCode],
      projectId,
    })

    const isUserCanEditProject =
      project?.authorId == me.id ||
      hasRoles({
        projectRoles: [ProjectRole.RoleProjectAdmin],
        projectId,
      })

    const isUserCanEditTask =
      project?.authorId == me.id ||
      hasRoles({
        projectRoles: [ProjectRole.RoleProjectAdmin],
        projectId,
      })

    const isUserCanEditForm =
      project?.authorId == me.id ||
      hasRoles({
        projectRoles: [ProjectRole.RoleProjectAdmin, ProjectRole.RoleProjectCustomer, ProjectRole.RoleProjectReader],
        projectId,
      })

    const isUserCanEditObservation =
      project?.authorId == me.id ||
      hasRoles({
        projectRoles: [ProjectRole.RoleProjectAdmin, ProjectRole.RoleProjectCustomer, ProjectRole.RoleProjectReader],
        projectId,
      })

    const isUserCanManageFolderAccess =
      project?.authorId == me.id ||
      hasRoles({
        // userRoles: [UserRole.RoleAdmin],
        projectRoles: [ProjectRole.RoleProjectAdmin],
        projectId,
      })

    const isUserCanViewPlanning =
      project?.authorId == me.id ||
      hasRoles({
        projectRoles: [ProjectRole.RoleProjectAdmin, ProjectRole.RoleProjectReader, ProjectRole.RoleProjectQrCode],
        projectId,
      })

    const isUserCanViewObservations =
      project?.authorId == me.id ||
      hasRoles({
        projectRoles: [ProjectRole.RoleProjectAdmin, ProjectRole.RoleProjectReader, ProjectRole.RoleProjectCustomer],
        projectId,
      })

    const isUserCanViewForms =
      project?.authorId == me.id ||
      hasRoles({
        projectRoles: [ProjectRole.RoleProjectAdmin, ProjectRole.RoleProjectReader, ProjectRole.RoleProjectCustomer],
        projectId,
      })

    const isProjectSubscriptionActive = project
      ? !project.isTrialPeriodExpired || project.subscription?.status == SubscriptionStatus.Active
      : false

    const canEditFormValues = (form: Form): boolean => {
      if (!isProjectSubscriptionActive || !isUserCanEditForm) return false
      if (isUserHasUserRoleAdmin) return true

      const { mode, allowAdminEdit } = form.formModel.validationConfig

      if (mode === FormModelValidationMode.OnChange) return true

      const hasAdminEditRights = allowAdminEdit && isUserCanEditProject
      const isDone = form.status === FormStatus.StatusDone

      // For submit mode, return true only if form is not done or user has admin rights
      return !isDone || hasAdminEditRights
    }

    const canEditFormStatus = (form: Form): boolean => {
      if (isUserHasQrCodeRole || !isProjectSubscriptionActive) return false
      if (isUserHasUserRoleAdmin) return true

      const { mode } = form.formModel.validationConfig

      // Always allow editing in onChange mode
      if (mode === FormModelValidationMode.OnChange) return true

      const isDone = form.status === FormStatus.StatusDone
      return !isDone
    }

    const canEditUserRbac = (userRbacTarget: ProjectUserRbac): boolean => {
      if (isUserHasQrCodeRole || !isProjectSubscriptionActive) return false
      if (isUserHasUserRoleAdmin || project?.authorId == me.id) return true

      return userRbacTarget.role !== ProjectRole.RoleProjectAdmin
    }

    return {
      isProjectSubscriptionActive,
      canEditProject: isUserHasUserRoleAdmin || (isUserCanEditProject && isProjectSubscriptionActive),
      canManageFolderAccess: isUserHasUserRoleAdmin || (isUserCanManageFolderAccess && isProjectSubscriptionActive),
      canViewPlanning: isUserHasUserRoleAdmin || isUserCanViewPlanning,
      canViewForms: isUserHasUserRoleAdmin || isUserCanViewForms,
      canViewObservations: isUserHasUserRoleAdmin || isUserCanViewObservations,
      canCommentTask: isUserHasUserRoleAdmin || (!isUserHasQrCodeRole && isProjectSubscriptionActive),
      canCommentForm: isUserHasUserRoleAdmin || (!isUserHasQrCodeRole && isProjectSubscriptionActive),
      canCommentObservation: isUserHasUserRoleAdmin || (!isUserHasQrCodeRole && isProjectSubscriptionActive),
      canEditTaskStatus: isUserHasUserRoleAdmin || (!isUserHasQrCodeRole && isProjectSubscriptionActive),
      canEditTask: isUserHasUserRoleAdmin || (isUserCanEditTask && isProjectSubscriptionActive),
      canEditForm: isUserHasUserRoleAdmin || (isUserCanEditForm && isProjectSubscriptionActive),
      canEditFormValues: canEditFormValues,
      canEditFormStatus: canEditFormStatus,
      canEditObservation: isUserHasUserRoleAdmin || (isUserCanEditObservation && isProjectSubscriptionActive),
      canEditObservationStatus: isUserHasUserRoleAdmin || (!isUserHasQrCodeRole && isProjectSubscriptionActive),
      canEditUserRbac: canEditUserRbac,
      isUserHasQrCodeRole,
    }
  }

  return { getProjectGuards, loading: projectLoading || meLoading }
}
